import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../stores/authStore';
import { api } from '../services/api';

interface QuizAttempt {
  id: string;
  category_name: string;
  score: number;
  total_questions: number;
  percentage: number;
  completed_at: string;
  duration_seconds: number;
}

interface BestScore {
  id: string;
  name: string;
  slug: string;
  best_percentage: number | null;
  attempts: number;
}

interface QuizSession {
  id: string;
  categoryId: string;
  categoryName: string;
  questions: any[];
  answers: Record<string, number>;
  flaggedQuestions: string[];
  currentQuestionIndex: number;
  createdAt: string;
  updatedAt: string;
}

export default function DashboardPage() {
  const navigate = useNavigate();
  const { user, logout } = useAuthStore();
  const [recentAttempts, setRecentAttempts] = useState<QuizAttempt[]>([]);
  const [bestScores, setBestScores] = useState<BestScore[]>([]);
  const [inProgressSessions, setInProgressSessions] = useState<QuizSession[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadProgress();
  }, []);

  const loadProgress = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const [attemptsRes, scoresRes, sessionsRes] = await Promise.all([
        api.get('/quizzes/attempts').catch(() => ({ data: [] })),
        api.get('/quizzes/best-scores').catch(() => ({ data: [] })),
        api.get('/quizzes/sessions').catch(() => ({ data: [] })),
      ]);
      
      setRecentAttempts(Array.isArray(attemptsRes.data) ? attemptsRes.data.slice(0, 5) : []);
      setBestScores(Array.isArray(scoresRes.data) ? scoresRes.data : []);
      setInProgressSessions(Array.isArray(sessionsRes.data) ? sessionsRes.data : []);
    } catch (err) {
      console.error('Failed to load progress:', err);
      setError('Unable to load progress data');
      // Set empty arrays as fallback
      setRecentAttempts([]);
      setBestScores([]);
      setInProgressSessions([]);
    } finally {
      setLoading(false);
    }
  };

  const totalAttempts = recentAttempts.length;
  const passedQuizzes = recentAttempts.filter(a => a.percentage >= 80).length;
  const averageScore = totalAttempts > 0
    ? Math.round(recentAttempts.reduce((sum, a) => sum + a.percentage, 0) / totalAttempts)
    : 0;

  const formatDate = (dateStr: string) => {
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    } catch {
      return 'Unknown date';
    }
  };

  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    return `${mins}m`;
  };

  return (
    <div className="min-h-screen">
      {/* Header */}
      <header className="border-b border-primary-500/20 bg-slate-900/95 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-primary-600 to-purple-600 rounded-lg flex items-center justify-center">
                <span className="text-xl">📊</span>
              </div>
              <div>
                <h1 className="text-lg font-bold">TaxOffice.AI Training</h1>
                <p className="text-xs text-slate-400">Tax Preparer Certification</p>
              </div>
            </div>

            <div className="flex items-center gap-4">
              <div className="text-right">
                <p className="text-sm font-medium">{user?.firstName} {user?.lastName}</p>
                <p className="text-xs text-slate-400">{user?.role}</p>
              </div>
              {(user?.role === 'location_admin' || user?.role === 'org_admin' || user?.role === 'super_admin') && (
                <button
                  onClick={() => navigate('/admin')}
                  className="btn btn-secondary text-sm"
                >
                  {user?.role === 'super_admin' && '👑 Super Admin'}
                  {user?.role === 'org_admin' && '🏢 Org Admin'}
                  {user?.role === 'location_admin' && '📍 Location Admin'}
                </button>
              )}
              <button
                onClick={() => navigate('/settings')}
                className="btn btn-secondary text-sm"
              >
                ⚙️ Settings
              </button>
              <button
                onClick={logout}
                className="btn btn-secondary text-sm"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h2 className="text-3xl font-bold mb-2">Welcome back, {user?.firstName}!</h2>
          <p className="text-slate-400">Continue your tax preparation training</p>
        </div>

        {/* Training Modules */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="card opacity-60 cursor-not-allowed relative">
            <div className="absolute top-4 right-4 bg-yellow-500/20 border border-yellow-500/50 text-yellow-400 text-xs px-2 py-1 rounded-full font-medium">
              Coming Soon
            </div>
            <div className="text-4xl mb-4">💬</div>
            <h3 className="text-xl font-bold mb-2">AI Tutor</h3>
            <p className="text-slate-400 text-sm mb-4">
              Ask questions and get instant answers about tax preparation
            </p>
            <div className="text-slate-500 text-sm font-medium">Available Soon</div>
          </div>

          <div 
            onClick={() => window.location.href = '/quiz'}
            className="card hover:border-primary-500/40 transition-colors cursor-pointer"
          >
            <div className="text-4xl mb-4">✓</div>
            <h3 className="text-xl font-bold mb-2">Quiz</h3>
            <p className="text-slate-400 text-sm mb-4">
              Test your knowledge with 100+ questions on tax credits
            </p>
            <div className="text-primary-400 text-sm font-medium">Take Quiz →</div>
          </div>

          <div 
            onClick={() => window.location.href = '/documents'}
            className="card hover:border-primary-500/40 transition-colors cursor-pointer"
          >
            <div className="text-4xl mb-4">📄</div>
            <h3 className="text-xl font-bold mb-2">Documents</h3>
            <p className="text-slate-400 text-sm mb-4">
              Practice with real tax forms and document analysis
            </p>
            <div className="text-primary-400 text-sm font-medium">View Documents →</div>
          </div>
        </div>

        {/* In-Progress Quizzes */}
        {!loading && inProgressSessions.length > 0 && (
          <div className="card mb-8 border-yellow-500/30 bg-yellow-500/5">
            <div className="flex items-center gap-2 mb-4">
              <span className="text-2xl">⏳</span>
              <h3 className="text-xl font-bold">In Progress</h3>
            </div>
            <div className="space-y-3">
              {inProgressSessions.map((session) => {
                const answeredCount = Object.keys(session.answers).length;
                const totalQuestions = session.questions.length;
                const progress = totalQuestions > 0 ? Math.round((answeredCount / totalQuestions) * 100) : 0;
                
                return (
                  <div key={session.id} className="flex items-center justify-between p-4 bg-slate-800/50 rounded-lg hover:bg-slate-800/70 transition-colors">
                    <div className="flex-1">
                      <p className="font-medium mb-1">{session.categoryName}</p>
                      <div className="flex items-center gap-3">
                        <p className="text-xs text-slate-400">
                          {answeredCount}/{totalQuestions} answered
                        </p>
                        <div className="flex-1 h-1.5 bg-slate-700 rounded-full overflow-hidden max-w-xs">
                          <div
                            className="h-full bg-yellow-500 transition-all"
                            style={{ width: `${progress}%` }}
                          />
                        </div>
                        <p className="text-xs text-yellow-400 font-medium">{progress}%</p>
                      </div>
                    </div>
                    <button
                      onClick={() => window.location.href = `/quiz?resume=${session.id}`}
                      className="btn btn-primary ml-4"
                    >
                      Resume Quiz
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Progress Stats */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          <div className="card">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-primary-500/20 rounded-lg flex items-center justify-center">
                <span className="text-xl">📝</span>
              </div>
              <div>
                <p className="text-2xl font-bold text-primary-400">{totalAttempts}</p>
                <p className="text-sm text-slate-400">Total Attempts</p>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-500/20 rounded-lg flex items-center justify-center">
                <span className="text-xl">✅</span>
              </div>
              <div>
                <p className="text-2xl font-bold text-green-400">{passedQuizzes}</p>
                <p className="text-sm text-slate-400">Quizzes Passed</p>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-purple-500/20 rounded-lg flex items-center justify-center">
                <span className="text-xl">📊</span>
              </div>
              <div>
                <p className="text-2xl font-bold text-purple-400">{averageScore}%</p>
                <p className="text-sm text-slate-400">Average Score</p>
              </div>
            </div>
          </div>
        </div>

        {/* Best Scores by Category */}
        {!loading && bestScores.length > 0 && (
          <div className="card mb-8">
            <h3 className="text-xl font-bold mb-4">Best Scores by Category</h3>
            <div className="space-y-3">
              {bestScores.map((score) => (
                <div key={score.id} className="flex items-center justify-between p-3 bg-slate-800/50 rounded-lg hover:bg-slate-800/70 transition-colors">
                  <div className="flex-1">
                    <p className="font-medium">{score.name}</p>
                    <p className="text-xs text-slate-400">{score.attempts} attempt{score.attempts !== 1 ? 's' : ''}</p>
                  </div>
                  <div className="text-right">
                    {score.best_percentage !== null ? (
                      <>
                        <p className={`text-lg font-bold ${score.best_percentage >= 80 ? 'text-green-400' : 'text-yellow-400'}`}>
                          {Math.round(score.best_percentage)}%
                        </p>
                        <p className="text-xs text-slate-400">
                          {score.best_percentage >= 80 ? '✓ Passed' : 'Keep trying'}
                        </p>
                      </>
                    ) : (
                      <p className="text-sm text-slate-500">Not attempted</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Recent Quiz Attempts */}
        {!loading && recentAttempts.length > 0 && (
          <div className="card mb-8">
            <h3 className="text-xl font-bold mb-4">Recent Quiz Attempts</h3>
            <div className="space-y-3">
              {recentAttempts.map((attempt) => (
                <div key={attempt.id} className="flex items-center justify-between p-3 bg-slate-800/50 rounded-lg hover:bg-slate-800/70 transition-colors">
                  <div className="flex-1">
                    <p className="font-medium">{attempt.category_name}</p>
                    <p className="text-xs text-slate-400">
                      {formatDate(attempt.completed_at)} • {formatDuration(attempt.duration_seconds)}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className={`text-lg font-bold ${attempt.percentage >= 80 ? 'text-green-400' : 'text-yellow-400'}`}>
                      {attempt.score}/{attempt.total_questions}
                    </p>
                    <p className="text-xs text-slate-400">{Math.round(attempt.percentage)}%</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* No Quiz Data State */}
        {!loading && recentAttempts.length === 0 && (
          <div className="card text-center py-12">
            <div className="text-6xl mb-4">🎯</div>
            <h3 className="text-xl font-bold mb-2">Start Your Training Journey</h3>
            <p className="text-slate-400 mb-6">Take your first quiz to begin tracking your progress</p>
            <button
              onClick={() => window.location.href = '/quiz'}
              className="btn btn-primary"
            >
              Take Your First Quiz
            </button>
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="card text-center py-12">
            <div className="text-4xl mb-4">⏳</div>
            <p className="text-slate-400">Loading your progress...</p>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="card bg-red-500/10 border-red-500/30 text-center py-8">
            <div className="text-4xl mb-4">⚠️</div>
            <p className="text-red-400">{error}</p>
            <button
              onClick={loadProgress}
              className="btn btn-secondary mt-4"
            >
              Try Again
            </button>
          </div>
        )}
      </main>
    </div>
  );
}
